import { useContext } from 'react';
import logTrailCardAddToListButtonTapped from '@alltrails/analytics/events/logTrailCardAddToListButtonTapped';
import NewTrailCard from '@alltrails/shared/components/TrailCard';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import { Trail } from 'types/Trails';
import useActivityDuration from 'hooks/useActivityDuration';
import { getTrailForTrailCard } from 'utils/cardInfo';
import logTrailCardClicked from '@alltrails/analytics/events/logTrailCardClicked';
import TrailCardLocation from '@alltrails/analytics/enums/TrailCardLocation';
import { PageContext, PageSource } from 'components/shared/Page/Page';
import useDisplayMetric from 'hooks/useDisplayMetric';
import CardLocation from '@alltrails/analytics/enums/CardLocation';
import { RiverAnalyticsData } from '@alltrails/modules/Lists/listAmplitudeHelpers';

function getTrailCardLocationFromPageSource(pageSource: PageSource): TrailCardLocation | null {
  switch (pageSource) {
    case PageSource.Explore:
      return TrailCardLocation.ExploreMapView;
    case PageSource.Seo:
      return TrailCardLocation.Seo;
    default:
      return null;
  }
}

type TrailCardProps = {
  variant?: 'standard' | 'wide';
  trail: Trail;
  handleCardClick?: () => void;
  handleFavoriteClick: () => void;
  isFavorite?: boolean;
  isCompleted?: boolean;
  isVerified?: boolean;
  messagingChannel?: {
    publish: (event: string, track: Trail) => void;
  };
  allowItemMoving?: boolean;
  dragHandleProps?: { [key: string]: any };
  allowItemDelete?: boolean;
  handleItemDeleteClick?: () => void;
  itemDeleteLabel?: string;
  displayRank?: number;
  className?: string;
  trailUrl?: string;
  maxPhotos?: number;
  handleClickAnalytics?: () => void;
  allowClickingCompletedBadge?: boolean;
  handleCompletedBadgeClick?: () => void;
  riverAnalyticsData?: RiverAnalyticsData;
  cardIndex?: number;
  updateCurrentlyHoveredResult?: (trail: Trail) => void;
  isNewMapsPage?: boolean;
};

const TrailCard = ({
  variant = 'standard',
  trail,
  handleCardClick,
  handleFavoriteClick,
  isFavorite,
  isCompleted,
  isVerified,
  messagingChannel,
  allowItemMoving,
  dragHandleProps,
  allowItemDelete,
  handleItemDeleteClick,
  itemDeleteLabel,
  displayRank,
  className,
  trailUrl,
  maxPhotos,
  handleClickAnalytics,
  allowClickingCompletedBadge,
  handleCompletedBadgeClick,
  riverAnalyticsData,
  cardIndex,
  updateCurrentlyHoveredResult,
  isNewMapsPage
}: TrailCardProps): JSX.Element => {
  const displayMetric = useDisplayMetric();
  const languageRegionCode = useLanguageRegionCode();
  const minutes = useActivityDuration(trail);
  const {
    globalState: { pageSource }
  } = useContext(PageContext);

  const deleteButton =
    allowItemDelete && handleItemDeleteClick && itemDeleteLabel
      ? {
          title: itemDeleteLabel,
          onClick: handleItemDeleteClick
        }
      : undefined;

  const handleTrailClick = () => {
    updateCurrentlyHoveredResult?.(undefined);
    if (pageSource === PageSource.Home) {
      logTrailCardClicked({
        detailed_card_location: CardLocation.WebHomepageRiver,
        river_filter_set: JSON.stringify(riverAnalyticsData.filters),
        river_index: riverAnalyticsData.riverIndex,
        river_item_index: cardIndex,
        river_section_type: riverAnalyticsData.riverSectionType,
        river_title_filter: riverAnalyticsData.riverTitleFilter,
        trail_id: String(trail.trail_id)
      });
    } else if (!isNewMapsPage) {
      logTrailCardClicked({ trail_id: String(trail.trail_id), card_location: getTrailCardLocationFromPageSource(pageSource) });
    }

    if (handleClickAnalytics) {
      handleClickAnalytics();
    }
    if (handleCardClick) {
      handleCardClick();
    }
  };

  const onFavoriteClick = () => {
    logTrailCardAddToListButtonTapped({ trail_id: trail.ID });
    handleFavoriteClick();
  };

  const handleMouseEnter = () => {
    messagingChannel?.publish?.('item.mouseenter', trail);
    updateCurrentlyHoveredResult?.(trail);
  };

  const handleMouseLeave = () => {
    messagingChannel?.publish?.('item.mouseleave', trail);
    updateCurrentlyHoveredResult?.(undefined);
  };

  const card = (
    <NewTrailCard
      variant={variant}
      languageRegionCode={languageRegionCode}
      onCardClick={handleTrailClick}
      onCardMouseEnter={handleMouseEnter}
      onCardMouseLeave={handleMouseLeave}
      onFavoriteClick={onFavoriteClick}
      isFavorite={isFavorite}
      isCompleted={isCompleted}
      isVerified={isVerified}
      trail={getTrailForTrailCard(trail, minutes)}
      url={trailUrl}
      blockNavigation={!!handleCardClick} // Navigation is handled by the click handler - this is existing behavior in TrailResultCard :(
      useMetric={displayMetric}
      maxPhotos={maxPhotos || trail.num_photos}
      dragHandleProps={allowItemMoving ? dragHandleProps : undefined}
      deleteButton={deleteButton}
      displayRank={displayRank}
      onCompletionBadgeClick={allowClickingCompletedBadge ? handleCompletedBadgeClick : undefined}
      lazyLoadImages
    />
  );

  return className ? <div className={className}>{card}</div> : card;
};

export default TrailCard;
